import React, { useState } from 'react';
import { Box, Text } from '@wix/design-system';
import { ChevronDown, ChevronUp } from '@wix/wix-ui-icons-common';
import s from './DynamicSelector.scss';

interface Option {
  value: string;
  label: string | React.ReactNode;
  disabled?: boolean;
}

export interface DynamicSelectorProps {
  /** Array of options to display */
  options: Option[];
  /** Callback when an option is selected */
  onSelect: (option: Option) => void;
  /** Currently selected option value */
  selectedValue?: string;
  /** Data hook for testing */
  dataHook?: string;
  /** Label text shown above options */
  label?: string;
  /** Number of options to show initially */
  initialVisibleCount?: number;
  /** Text for the action button */
  actionText?: string;
  /** Icon for the action button */
  actionIcon?: React.ReactNode;
  /** Callback when action button is clicked */
  onAction?: () => void;
  /** Loading state */
  isLoading?: boolean;
  /** Additional class name */
  className?: string;
}

const ToggleButton: React.FC<{
  isExpanded: boolean;
  onClick: () => void;
  disabled?: boolean;
  dataHook?: string;
}> = ({ isExpanded, onClick, disabled, dataHook }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={s.toggleButton}
    data-hook={`${dataHook}-toggle`}
    aria-expanded={isExpanded}
  >
    <Text size="small" className={s.toggleText}>
      {isExpanded ? 'Show less' : 'Show more'}
    </Text>
    {isExpanded ? <ChevronUp className={s.chevron} /> : <ChevronDown className={s.chevron} />}
  </button>
);

const ActionButton: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  text?: string;
  dataHook?: string;
}> = ({ onClick, disabled, icon, text, dataHook }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={s.actionButton}
    data-hook={`${dataHook}-action`}
  >
    {icon && <span className={s.actionIcon}>{icon}</span>}
    {text && (
      <Text size="small" className={s.actionText}>
        {text}
      </Text>
    )}
  </button>
);

export const DynamicSelector: React.FC<DynamicSelectorProps> = ({
  options,
  onSelect,
  selectedValue,
  dataHook,
  label,
  initialVisibleCount = 5,
  actionText,
  actionIcon,
  onAction,
  isLoading = false,
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleOptions = isExpanded ? options : options.slice(0, initialVisibleCount);
  const hasMoreOptions = options.length > initialVisibleCount;

  const handleOptionClick = (option: Option) => {
    if (!isLoading && !option.disabled) {
      onSelect(option);
    }
  };

  return (
    <div className={`${s.root} ${className || ''}`} role="group" aria-labelledby={`${dataHook}-label`}>
      <div className={s.header}>
        {label && (
          <Text size="small" id={`${dataHook}-label`}>
            {label}
          </Text>
        )}

        <Box gap={2}>
          {onAction && (
            <ActionButton
              onClick={onAction}
              disabled={isLoading}
              icon={actionIcon}
              text={actionText}
              dataHook={dataHook}
            />
          )}

          {hasMoreOptions && (
            <ToggleButton
              isExpanded={isExpanded}
              onClick={() => setIsExpanded(!isExpanded)}
              disabled={isLoading}
              dataHook={dataHook}
            />
          )}
        </Box>
      </div>

      <Box direction="horizontal" className={s.optionsContainer}>
        {visibleOptions.map((option, index) => (
          <button
            key={option.value}
            onClick={() => handleOptionClick(option)}
            disabled={isLoading || option.disabled}
            className={`${s.option} ${option.value === selectedValue ? s.selected : ''}`}
            data-hook={`${dataHook}-${index}`}
            aria-selected={option.value === selectedValue}
            role="option"
          >
            {typeof option.label === 'string' ? <Text>{option.label}</Text> : option.label}
          </button>
        ))}
      </Box>
    </div>
  );
};
